/* Cali Poke House — custom horizontal scrollbar for carousels and scroll rows.
   Renders an always-visible track + draggable thumb beneath a
   [data-scroll-container], synced to its scroll position.

   Progressive enhancement: the <carousel-scrollbar> ships `hidden`, and JS only
   reveals it once it confirms the container actually overflows in the current
   viewport. No overflow (e.g. a carousel that is a plain grid on desktop) → the
   bar stays hidden. Without JS, no bar is shown. */
(function () {
  if (customElements.get('carousel-scrollbar')) return;

  const clamp = (n, min, max) => Math.min(Math.max(n, min), max);

  class CarouselScrollbar extends HTMLElement {
    connectedCallback() {
      this.scroller =
        (this.parentElement && this.parentElement.querySelector('[data-scroll-container]')) ||
        this.previousElementSibling;
      this.track = this.querySelector('.cscrollbar__track');
      this.thumb = this.querySelector('.cscrollbar__thumb');
      if (!this.scroller || !this.track || !this.thumb) return;

      this._update = this.update.bind(this);
      this.scroller.addEventListener('scroll', this._update, { passive: true });
      window.addEventListener('resize', this._update, { passive: true });
      window.addEventListener('load', this._update);

      if ('ResizeObserver' in window) {
        this._ro = new ResizeObserver(this._update);
        this._ro.observe(this.scroller);
        if (this.scroller.firstElementChild) this._ro.observe(this.scroller.firstElementChild);
      }

      this.thumb.addEventListener('pointerdown', (e) => this.startDrag(e));
      this.track.addEventListener('pointerdown', (e) => this.trackClick(e));

      this.update();
      // Recompute once async content (web fonts, lazy images) has settled.
      requestAnimationFrame(this._update);
    }

    disconnectedCallback() {
      if (this.scroller) this.scroller.removeEventListener('scroll', this._update);
      window.removeEventListener('resize', this._update);
      window.removeEventListener('load', this._update);
      if (this._ro) this._ro.disconnect();
    }

    get scrollableWidth() {
      return this.scroller.scrollWidth - this.scroller.clientWidth;
    }

    isScrollable() {
      const overflowX = getComputedStyle(this.scroller).overflowX;
      return (overflowX === 'auto' || overflowX === 'scroll') && this.scrollableWidth > 1;
    }

    update() {
      if (!this.isScrollable()) {
        this.hidden = true;
        return;
      }
      this.hidden = false;

      const trackWidth = this.track.clientWidth;
      const viewRatio = this.scroller.clientWidth / this.scroller.scrollWidth;
      const thumbWidth = Math.max(Math.round(trackWidth * viewRatio), 32);
      const maxX = trackWidth - thumbWidth;
      const progress = clamp(this.scroller.scrollLeft / this.scrollableWidth, 0, 1);

      this.thumb.style.width = thumbWidth + 'px';
      this.thumb.style.transform = 'translateX(' + Math.round(maxX * progress) + 'px)';
    }

    startDrag(e) {
      e.preventDefault();
      const maxX = this.track.clientWidth - this.thumb.clientWidth;
      if (maxX <= 0) return;

      const startX = e.clientX;
      const startScroll = this.scroller.scrollLeft;
      const scrollable = this.scrollableWidth;
      this.thumb.setPointerCapture(e.pointerId);
      this.classList.add('is-dragging');

      const onMove = (ev) => {
        const dx = ev.clientX - startX;
        this.scroller.scrollLeft = startScroll + (dx / maxX) * scrollable;
      };
      const onUp = (ev) => {
        if (this.thumb.hasPointerCapture(ev.pointerId)) this.thumb.releasePointerCapture(ev.pointerId);
        this.classList.remove('is-dragging');
        this.thumb.removeEventListener('pointermove', onMove);
        this.thumb.removeEventListener('pointerup', onUp);
        this.thumb.removeEventListener('pointercancel', onUp);
      };

      this.thumb.addEventListener('pointermove', onMove);
      this.thumb.addEventListener('pointerup', onUp);
      this.thumb.addEventListener('pointercancel', onUp);
    }

    trackClick(e) {
      if (e.target === this.thumb) return;
      const rect = this.track.getBoundingClientRect();
      const maxX = this.track.clientWidth - this.thumb.clientWidth;
      if (maxX <= 0) return;

      const target = e.clientX - rect.left - this.thumb.clientWidth / 2;
      const progress = clamp(target / maxX, 0, 1);
      const reduce = matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.scroller.scrollTo({ left: progress * this.scrollableWidth, behavior: reduce ? 'auto' : 'smooth' });
    }
  }

  customElements.define('carousel-scrollbar', CarouselScrollbar);
})();
